import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Check, Sparkles } from 'lucide-react';

const Offers = () => {
  const { t } = useLanguage();

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="offers" className="py-24 bg-gradient-to-br from-teal-50 via-cyan-50 to-amber-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-300 rounded-full mix-blend-multiply filter blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in-up">
          <Badge className="mb-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2 text-base">
            <Sparkles className="w-4 h-4 inline mr-2" />
            {t.offers.badge}
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {t.offers.title}
          </h2>
          <p className="text-xl text-gray-600">{t.offers.subtitle}</p>
        </div>

        {/* Offers Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {t.offers.items.map((offer, index) => (
            <Card
              key={index}
              className="group relative hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 hover:border-teal-500 overflow-hidden bg-white pt-6"
            >
              {/* Discount Badge */}
              <div className="absolute top-4 right-4 z-10">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg rotate-3 group-hover:rotate-6 transition-transform">
                  {offer.discount}*
                </div>
              </div>

              <CardHeader>
                <CardTitle className="text-2xl mb-2 pr-20">{offer.title}</CardTitle>
                <CardDescription className="text-base">{offer.description}</CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Pricing */}
                <div className="space-y-1">
                  <div className="text-gray-400 line-through text-lg">{offer.originalPrice}</div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent pb-0.5">
                    {offer.newPrice}*
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2 pt-4">
                  {offer.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-600">
                      <Check className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Validity */}
                <div className="pt-4 space-y-1 border-t">
                  <div className="text-sm text-amber-600 font-medium">
                    {offer.validity}
                  </div>
                  <div className="text-xs text-gray-500 italic">
                    *Conditions apply | تطبق الشروط والأحكام*
                  </div>
                </div>
              </CardContent>

              <CardFooter>
                <Button
                  onClick={scrollToContact}
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {t.nav.getStarted}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Offers;